import { QuestionnaireResultsFilter, QuestionnaireScore, SittingStatus } from '@stores/QuestionnaireAdminStore';

export const sittingStatusLabels: Record<SittingStatus, string> = {
  [SittingStatus.Upcoming]: 'Upcoming',
  [SittingStatus.Available]: 'Open',
  [SittingStatus.Completed]: 'Completed',
  [SittingStatus.Expired]: 'Missed',
};

export const sittingStatusColors: Record<SittingStatus, 'default' | 'info' | 'success' | 'warning'> = {
  [SittingStatus.Upcoming]: 'default',
  [SittingStatus.Available]: 'info',
  [SittingStatus.Completed]: 'success',
  [SittingStatus.Expired]: 'warning',
};

const TOTAL_SCALE = 'TOTAL';

/** One instrument's scores: its total, if it has one, and its subscales. */
export interface ScoreGroup {
  // Null for scores of the whole questionnaire rather than one instrument.
  instrumentCode: string | null;
  total: QuestionnaireScore | null;
  subscales: QuestionnaireScore[];
}

/** Groups scores by instrument, in the order the instruments first appear, splitting out each total. */
export const groupScores = (scores: QuestionnaireScore[]): ScoreGroup[] => {
  const groups: ScoreGroup[] = [];
  for (const score of scores) {
    let group = groups.find((existing) => existing.instrumentCode === score.instrumentCode);
    if (!group) {
      group = { instrumentCode: score.instrumentCode, total: null, subscales: [] };
      groups.push(group);
    }
    if (score.scaleCode === TOTAL_SCALE && !group.total) group.total = score;
    else group.subscales.push(score);
  }
  return groups;
};

/**
 * Splits grouped scores for display: instruments with subscales get their own lines, and one-question instruments,
 * which have only a total, share a single line after them, so a long questionnaire stays readable.
 */
export const arrangeScores = (
  scores: QuestionnaireScore[]
): { detailed: ScoreGroup[]; singles: QuestionnaireScore[] } => {
  const groups = groupScores(scores);
  return {
    detailed: groups.filter((group) => group.subscales.length > 0),
    singles: groups.flatMap((group) => (group.subscales.length === 0 && group.total ? [group.total] : [])),
  };
};

/** The scorer's name for a score, falling back to its codes, e.g. "PHQ4 total" or "PHQ2". */
export const scoreLabel = (score: QuestionnaireScore): string => {
  if (score.scaleName) return score.scaleName;
  if (score.scaleCode === TOTAL_SCALE) return score.instrumentCode ? `${score.instrumentCode} total` : 'Total';
  return score.scaleCode;
};

/** e.g. POSITIVE_SCREEN reads as "Positive screen". */
export const severityBandLabel = (severityBand: string): string => {
  const words = severityBand.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

type SeverityColor = 'default' | 'success' | 'info' | 'warning' | 'error';

// Green is fine, blue is worth noting, orange is worth a look, red is worth following up. Bands from scorers added
// later show grey until they're given a colour here.
const severityBandColors: Record<string, SeverityColor> = {
  NORMAL: 'success',
  MILD: 'info',
  MODERATE: 'warning',
  SEVERE: 'error',
  NEGATIVE_SCREEN: 'success',
  POSITIVE_SCREEN: 'error',
};

export const severityBandColor = (severityBand: string): SeverityColor => severityBandColors[severityBand] ?? 'default';

const filterKeys: (keyof QuestionnaireResultsFilter)[] = ['cohortId', 'formId', 'timepointId'];

/** The results filter in the page's URL, so a filtered view can be bookmarked or linked to. */
export const filterFromSearchParams = (searchParams: URLSearchParams): QuestionnaireResultsFilter =>
  Object.fromEntries(
    filterKeys
      .map((key) => [key, Number(searchParams.get(key))] as const)
      .filter(([, value]) => Number.isInteger(value) && value > 0)
  );

export const searchParamsFromFilter = (filter: QuestionnaireResultsFilter): Record<string, string> =>
  Object.fromEntries(filterKeys.filter((key) => filter[key] !== undefined).map((key) => [key, String(filter[key])]));

export const hasAnyFilter = (filter: QuestionnaireResultsFilter): boolean =>
  filterKeys.some((key) => filter[key] !== undefined);
